import { styled } from "@mui/material";
import { bp, buttonBorder, buttonGradient, fluid, font, w } from "../../styles/landing-tokens";

/**
 * Horizontally centred content column. `width` is the Figma *content* width of
 * the section — the 40px gutter is added on top so the content lands on the
 * exact Figma x coordinate at a 1920 viewport.
 */
export const Container = styled("div")<{ width?: number }>(({ width = 1544 }) => ({
  width: "100%",
  maxWidth: width + 80,
  margin: "0 auto",
  padding: "0 40px",

  [bp.sm]: {
    padding: "0 20px",
  },
}));

/** The three-line display headings ("Interdisciplinary / by practice, / …"). */
export const DisplayHeading = styled("h2")({
  fontFamily: font.sans,
  fontWeight: 800,
  fontSize: fluid(38, 75.2),
  lineHeight: 1.04,
  letterSpacing: "-0.033em",
  color: "#FFFFFF",
  whiteSpace: "pre-line",
  margin: 0,
});

/** Small all-caps label — "A personal note", "Selected Chapters", … */
export const Eyebrow = styled("span")<{ opacity?: number }>(({ opacity = 0.35 }) => ({
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 12.5,
  lineHeight: "18.8px",
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: w(opacity),
  whiteSpace: "nowrap",
}));

/**
 * Filled gradient CTA — "Get in Touch", "Request a copy". Figma: 282×57.
 * The label is centred on the button and any trailing icon is pinned to the
 * right edge rather than sitting next to the text (Figma: icon 18×18 at
 * 10px from the right, 11px from the top).
 */
export const PrimaryButton = styled("a")<{ from?: string }>(({ from }) => ({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 282,
  height: 57,
  padding: "16.6px 40px",
  borderRadius: 9.5,
  background: buttonGradient(from),
  /**
   * Figma strokes this INSIDE the shape, so the line composites over the
   * gradient. A CSS `border` would instead sit over the gradient's edge
   * (background-origin is padding-box) and read as a bright outline.
   */
  border: "none",
  boxShadow: `inset 0 0 0 1.28px ${buttonBorder}`,

  fontFamily: font.ui,
  fontWeight: 600,
  fontSize: 15.3,
  lineHeight: "17.4px",
  letterSpacing: "0.011em",
  color: "#FFFFFF",
  textDecoration: "none",
  cursor: "pointer",
  transition: "filter .2s ease, transform .2s ease",

  /** Trailing icon sits at the button's right edge, not beside the label. */
  "& > svg": {
    position: "absolute",
    right: 10,
    top: 11,
  },

  "&:hover": {
    filter: "brightness(1.25)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },

  [bp.sm]: {
    width: "100%",
  },
}));

/** Translucent blurred button — "Read the Books", "Watch". */
export const GhostButton = styled("a")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: 240,
  height: 57,
  borderRadius: 9.5,
  background: "rgba(50, 53, 59, 0.2)",
  /** Figma strokes INSIDE — see the note on PrimaryButton. */
  border: "none",
  boxShadow: `inset 0 0 0 1.28px ${buttonBorder}`,
  backdropFilter: "blur(15.3px)",
  WebkitBackdropFilter: "blur(15.3px)",
  fontFamily: font.ui,
  fontWeight: 600,
  fontSize: 15.3,
  letterSpacing: "0.012em",
  color: "#FFFFFF",
  textDecoration: "none",
  cursor: "pointer",
  transition: "background .2s ease",

  "&:hover": {
    background: "rgba(50, 53, 59, 0.4)",
  },

  [bp.sm]: {
    width: "100%",
  },
});

/**
 * Numbered discipline row: `01 — Finance — DERIVATIVES & STRUCTURED PRODUCTS`.
 * Used by both the hero and the personal-note column, which differ only in
 * type scale and whether a hairline separator is drawn.
 */
export const DisciplineRow = styled("div")<{ divided?: boolean }>(({ divided }) => ({
  display: "flex",
  alignItems: "center",
  gap: 20.1,
  padding: divided ? "20.1px 0" : "11px 0",
  borderBottom: divided ? `1.25px solid ${w(0.05)}` : "none",

  "& .no": {
    fontFamily: font.sans,
    fontWeight: 600,
    fontSize: divided ? 12.5 : 13.7,
    lineHeight: divided ? "18.8px" : "20.6px",
    letterSpacing: "0.1em",
    color: w(0.3),
  },
  "& .name": {
    flex: 1,
    fontFamily: font.sans,
    fontWeight: 500,
    fontSize: divided ? 18.8 : 19.2,
    lineHeight: 1.5,
    letterSpacing: divided ? "-0.02em" : 0,
    color: divided ? "#FFFFFF" : w(0.8),
  },
  "& .meta": {
    fontFamily: font.sans,
    fontWeight: divided ? 500 : 600,
    fontSize: divided ? 13.8 : 13.7,
    lineHeight: divided ? "20.7px" : "20.6px",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: w(divided ? 0.35 : 0.35),
    whiteSpace: "nowrap",
  },

  [bp.sm]: {
    gap: 12,
    "& .name": { fontSize: 16 },
    "& .meta": { fontSize: 11, letterSpacing: "0.1em" },
  },
}));
